import { AsyncSelect, Space, Text } from '@procurify/ui'

import { UserAvatar } from '@webapp/components/UserAvatar'
import { useProcurifyIntl } from '@webapp/hooks'
// Self-contained Select fetches its own remote options (wiki/How-to-Build-Selects.md)
// eslint-disable-next-line @procurify/frontend-architecture-rules/layer-no-data-access-import
import { userRepository } from '@webapp/platform/iam/data-access/repositories/UserRepository'
import { Permissions } from '@webapp/platform/iam/entities/enums/PermissionEnum'
import { User } from '@webapp/platform/iam/entities/User'
import messages from './messages'
import { OptionEmailStyled } from './styles/OptionEmailStyled'

// Server-side picker filter: only users holding at least one of the four
// report sub-permissions appear (BHV-015). OR-joined into the `permission`
// query param.
const REPORT_ACCESS_USER_FILTER_PERMISSIONS = [
  Permissions.REPORTS_VIEW_EXPENSES_BY_DEPARTMENT,
  Permissions.REPORTS_VIEW_ORDERS_BY_DEPARTMENT,
  Permissions.REPORTS_VIEW_ORDERS_BY_USER,
  Permissions.REPORTS_VIEW_ORDERS_BY_VENDOR,
] as const

export interface IReportAccessUserSelectProps {
  value: User | null
  onChange: (user: User | null) => void
  isDisabled?: boolean
}

/**
 * Self-contained AsyncSelect over `/api/v3/users/` (ADR-3). The basic user
 * endpoint used by the shared UserSelect does not expose `role`/`authId`,
 * both of which this picker needs (Superuser exclusion + grant payloads).
 */
export const ReportAccessUserSelect = ({
  value,
  onChange,
  isDisabled = false,
}: IReportAccessUserSelectProps) => {
  const intl = useProcurifyIntl()

  const loadOptions = (
    inputValue: string,
    pagination: { page: number },
    callback: (args: {
      options: User[]
      pagination: { hasMore: boolean; page: number }
    }) => void
  ) => {
    userRepository
      .list(true, {
        search: inputValue || undefined,
        page: pagination.page,
        orderBy: 'firstName',
        permission: REPORT_ACCESS_USER_FILTER_PERMISSIONS.join('|'),
      })
      .then((res) => {
        callback({
          options: res.data
            .map((user) => new User(user))
            // Superusers are excluded client-side (BHV-015)
            .filter((user) => !user.role?.isSuperuser),
          pagination: {
            hasMore: res.pagination.page < res.pagination.numPages,
            page: pagination.page + 1,
          },
        })
      })
      .catch(() => null)
  }

  return (
    <AsyncSelect<User>
      cacheOptions
      loadOptions={loadOptions}
      placeholder={intl.formatMessage(messages.placeholder)}
      value={value}
      onChange={(option) => onChange(option ?? null)}
      getOptionValue={(option) => option.id.toString()}
      getOptionLabel={(option) => option.fullName}
      formatOptionLabel={(option, { context }) =>
        context === 'menu' ? (
          <Space direction='horizontal' justify='space-between' align='center'>
            <Space direction='horizontal' size='small' align='center'>
              <UserAvatar
                size='small'
                circular
                url={option.profileImage || undefined}
              />
              <Text inline bold>
                {option.fullName}
              </Text>
            </Space>
            <OptionEmailStyled>{option.email}</OptionEmailStyled>
          </Space>
        ) : (
          option.fullName
        )
      }
      isClearable
      isDisabled={isDisabled}
    />
  )
}

ReportAccessUserSelect.displayName = 'ReportAccessUserSelect'
